import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPreferredScrollBehavior } from '@/utils/motion';
import { moTravelRouteQueryOptions } from '../api/moTravel.api';
import type { MapOn } from '../constants/moTravel.constants';
import type { WiderPeriodAction } from '../types/moTravel.types';
import { buildDayRoutes } from '../utils/dayRoutes';
import { formatPeriodLabel, isMultiDayPeriod } from '../utils/moTravelPeriod';
import { findStopDetail } from '../utils/routeFocus';
import { buildStartPlaces } from '../utils/startPlaces';
import { isKeyOnDay } from '../utils/stopKey';
import { useIsStackedLayout } from './useIsStackedLayout';
import { useMoTravelDetailSearch } from './useMoTravelDetailSearch';
import { useMoTravelShortcuts } from './useMoTravelShortcuts';
import { useRevealMapAndJourneyPanel } from './useRevealMapAndJourneyPanel';
import { useWaypointFocus } from './useWaypointFocus';

const RESET_KEY_SEPARATOR = '#';

const WIDER_PERIOD_BY_MAP_ON: Record<MapOn, { mapOn: MapOn; label: string } | null> = {
  DAILY: { mapOn: 'WEEKLY', label: 'View this week' },
  WEEKLY: { mapOn: 'MONTHLY', label: 'View this month' },
  MONTHLY: null,
};

export type JourneyOverlay = 'stepper' | 'startButton' | 'startPicker' | 'multiDayHint' | null;

interface JourneyOverlayInput {
  isJourneyStepperVisible: boolean;
  isMultiDay: boolean;
  canStepJourney: boolean;
  hasStartPlaces: boolean;
}

function getJourneyOverlay({
  isJourneyStepperVisible,
  isMultiDay,
  canStepJourney,
  hasStartPlaces,
}: JourneyOverlayInput): JourneyOverlay {
  if (isJourneyStepperVisible) return 'stepper';
  if (isMultiDay) return hasStartPlaces ? 'startPicker' : 'multiDayHint';
  if (canStepJourney) return 'startButton';
  return null;
}

export function useMoTravelDetail(userId: number) {
  const searchState = useMoTravelDetailSearch();
  const { period, selectedDay, handlePeriodChange, handleDaySelect } = searchState;
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const journeyPanelRef = useRef<HTMLElement>(null);
  const isStackedLayout = useIsStackedLayout();

  const routeQuery = useQuery(moTravelRouteQueryOptions(userId, period));
  const route = routeQuery.data;
  const dayRoutes = useMemo(() => buildDayRoutes(route?.days ?? []), [route]);

  const displayedRoutes = useMemo(
    () =>
      selectedDay ? dayRoutes.filter((dayRoute) => dayRoute.day.date === selectedDay) : dayRoutes,
    [dayRoutes, selectedDay],
  );
  const startPlaces = useMemo(() => buildStartPlaces(displayedRoutes), [displayedRoutes]);

  const waypointFocus = useWaypointFocus({
    dayRoutes,
    displayedRoutes,
    resetKey: [userId, period.date, period.mapOn, selectedDay].join(RESET_KEY_SEPARATOR),
  });

  const { focusedKey, journeyEntries } = waypointFocus;
  const focusedEntry = useMemo(
    () => journeyEntries.find((entry) => entry.key === focusedKey) ?? null,
    [journeyEntries, focusedKey],
  );
  const focusedStopDetail = useMemo(
    () => (isStackedLayout ? findStopDetail(displayedRoutes, focusedKey) : null),
    [isStackedLayout, displayedRoutes, focusedKey],
  );
  useRevealMapAndJourneyPanel(journeyPanelRef, mapSectionRef, isStackedLayout ? focusedKey : null);

  function handleEscape() {
    if (focusedKey !== null) waypointFocus.handleStopFocusClear();
  }

  useMoTravelShortcuts({
    canStepJourney: waypointFocus.canStepJourney,
    onEscape: handleEscape,
    onJourneyStep: waypointFocus.handleJourneyStep,
  });

  function handleTimelineStopSelect(waypointKey: string) {
    waypointFocus.handleWaypointFocus(waypointKey);
    mapSectionRef.current?.scrollIntoView({
      behavior: getPreferredScrollBehavior(),
      block: 'nearest',
    });
  }

  const widerPeriod = WIDER_PERIOD_BY_MAP_ON[period.mapOn];
  const widerPeriodAction: WiderPeriodAction | null = widerPeriod
    ? {
        label: widerPeriod.label,
        onClick: () => handlePeriodChange({ mapOn: widerPeriod.mapOn }),
      }
    : null;

  const focusedDate = route?.days.find((day) => isKeyOnDay(focusedKey, day.date))?.date ?? null;
  const statsDate = isMultiDayPeriod(period.mapOn) ? (selectedDay ?? focusedDate) : null;

  function handleShowWholePeriod() {
    handleDaySelect(null);
    waypointFocus.handleStopFocusClear();
  }

  return {
    ...searchState,
    ...waypointFocus,
    mapSectionRef,
    journeyPanelRef,
    focusedEntry,
    focusedStopDetail,
    isStackedLayout,
    route,
    routeQuery,
    dayRoutes,
    displayedRoutes,
    periodLabel: formatPeriodLabel(period.date, period.mapOn),
    widerPeriodAction,
    journeyOverlay: getJourneyOverlay({
      isJourneyStepperVisible: waypointFocus.isJourneyStepperVisible,
      isMultiDay: waypointFocus.isMultiDay,
      canStepJourney: waypointFocus.canStepJourney,
      hasStartPlaces: startPlaces.length > 0,
    }),
    startPlaces,
    canReturnToStartPicker: waypointFocus.isMultiDay && startPlaces.length > 0,
    statsDate,
    handleTimelineStopSelect,
    handleShowWholePeriod,
  };
}
